// per docs/shape-uc27-alert-engine.md §Backend §Rule DSL
// per docs/caiet-uc-spec.md §UC27 §UC-ALRT-02 §UC-ALRT-03 §UC-ALRT-05
import { z } from 'zod';
import {
  ALERT_BUILDER_COMPARISONS,
  ALERT_BUILDER_FUNCTIONS,
  ALERT_BUILDER_OPERATORS,
  ALERT_KIND_BOUNDS,
  REPORTING_COVERAGE_SCOPES,
} from './alert-fields';

export const alertSeverityEnum = z.enum(['critical', 'warning', 'info']);
export type AlertSeverityValue = z.infer<typeof alertSeverityEnum>;

export const alertEventStatusEnum = z.enum(['pending', 'firing', 'resolved', 'silenced']);
export type AlertEventStatusValue = z.infer<typeof alertEventStatusEnum>;

export const notificationChannelKindEnum = z.enum(['email', 'webhook', 'inapp', 'mnotify']);
export type NotificationChannelKindValue = z.infer<typeof notificationChannelKindEnum>;

// Rule DSL — strictly safelist-validated kinds.
//
// ALERT_RULE_KINDS is the one place the vocabulary lives. It had six literal copies —
// the entity, the compiler's switch, the YAML import's validKinds, the seed file, the
// rule form's RuleKind and the policy form's RULE_KINDS — and adding a member meant
// finding all six. Spec 057 T005 collapsed them; a seventh copy is a defect, not a
// shortcut.
export const ALERT_RULE_KINDS = [
  'stock_below_threshold',
  'lot_expiring',
  'no_report',
  'duplicate_report',
  'sql',
  'builder',
  // The agreed alerts A4, A5, A9, A10 and A12 (specs/060-agreed-alerts/plan.md C5).
  'watched_zero_stock',
  'watched_depletion_risk',
  'sudden_drop',
  'watched_export',
  'reporting_coverage',
] as const;

export type AlertRuleKind = (typeof ALERT_RULE_KINDS)[number];
export const stockBelowThresholdParams = z
  .object({
    medicineCode: z.string().min(1).max(64),
    minQuantity: z.number().int().nonnegative(),
    groupBy: z.array(z.enum(['site_id', 'registrar_id', 'locality_code'])).optional(),
  })
  .strict();

export const lotExpiringParams = z
  .object({
    daysBefore: z.number().int().min(1).max(365),
  })
  .strict();

export const noReportParams = z
  .object({
    withinHours: z.number().int().min(1).max(720),
    exceptSiteIds: z.array(z.string().uuid()).default([]),
    exceptSiteTags: z.array(z.string().min(1).max(40)).default([]),
  })
  .strict();

export const duplicateReportParams = z
  .object({
    windowHours: z.number().int().min(1).max(168),
  })
  .strict();

export const sqlParams = z
  .object({
    templateId: z.string().min(1).max(64),
    args: z.record(z.union([z.string(), z.number(), z.boolean()])).default({}),
  })
  .strict();

// The agreed-alert kinds (specs/060-agreed-alerts/plan.md C5). A watched group is named by
// its id; whether it exists, and has the safety threshold A5 and A10 compare with, is
// checked against catalog when the rule is saved.
const boundedInt = (bounds: { min: number; max: number; default: number }) =>
  z.number().int().min(bounds.min).max(bounds.max).default(bounds.default);

export const watchedZeroStockParams = z.object({ groupId: z.string().uuid() }).strict();

export const watchedDepletionRiskParams = z.object({ groupId: z.string().uuid() }).strict();

export const suddenDropParams = z
  .object({
    dropPercent: boundedInt(ALERT_KIND_BOUNDS.dropPercent),
    windowHours: boundedInt(ALERT_KIND_BOUNDS.dropWindowHours),
  })
  .strict();

export const watchedExportParams = z
  .object({
    groupId: z.string().uuid(),
    windowMinutes: boundedInt(ALERT_KIND_BOUNDS.exportWindowMinutes),
  })
  .strict();

export const reportingCoverageParams = z
  .object({
    minPercent: boundedInt(ALERT_KIND_BOUNDS.coverageMinPercent),
    windowHours: boundedInt(ALERT_KIND_BOUNDS.coverageWindowHours),
    scope: z.enum(REPORTING_COVERAGE_SCOPES).default('NATIONAL_AND_REGION'),
  })
  .strict();

// The builder kind of spec 057 (docs/shape-ops-agreed-alerts.md §2.1). The shape only:
// which field, function and operator a rule may use is the catalogue's to say, and the
// compiler refuses each with its own code, so these bounds are sizes, not vocabulary.
const builderValueSchema = z.union([z.string().max(200), z.number().finite()]);

export const alertBuilderFilterSchema = z
  .object({
    field: z.string().min(1).max(80),
    op: z.enum(ALERT_BUILDER_OPERATORS),
    value: z.union([builderValueSchema, z.array(builderValueSchema).max(500)]),
  })
  .strict();

export const alertBuilderParamsSchema = z
  .object({
    sources: z.array(z.string().min(1).max(40)).min(1).max(10),
    measure: z
      .object({ field: z.string().min(1).max(80), fn: z.enum(ALERT_BUILDER_FUNCTIONS) })
      .strict(),
    groupBy: z.array(z.string().min(1).max(80)).max(20).default([]),
    context: z.array(z.string().min(1).max(80)).max(20).default([]),
    filters: z.array(alertBuilderFilterSchema).max(30).default([]),
    window: z
      .object({
        amount: z.number().int().min(1).max(8760),
        unit: z.enum(['hours', 'days']),
        field: z.string().min(1).max(80).optional(),
      })
      .strict()
      .nullable()
      .default(null),
    comparison: z
      .object({ op: z.enum(ALERT_BUILDER_COMPARISONS), value: z.number().finite() })
      .strict(),
  })
  .strict();
export type AlertBuilderParams = z.infer<typeof alertBuilderParamsSchema>;
export type AlertBuilderFilter = z.infer<typeof alertBuilderFilterSchema>;

export const alertRuleExpressionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('stock_below_threshold'), params: stockBelowThresholdParams }),
  z.object({ kind: z.literal('lot_expiring'), params: lotExpiringParams }),
  z.object({ kind: z.literal('no_report'), params: noReportParams }),
  z.object({ kind: z.literal('duplicate_report'), params: duplicateReportParams }),
  z.object({ kind: z.literal('sql'), params: sqlParams }),
  z.object({ kind: z.literal('builder'), params: alertBuilderParamsSchema }),
  z.object({ kind: z.literal('watched_zero_stock'), params: watchedZeroStockParams }),
  z.object({ kind: z.literal('watched_depletion_risk'), params: watchedDepletionRiskParams }),
  z.object({ kind: z.literal('sudden_drop'), params: suddenDropParams }),
  z.object({ kind: z.literal('watched_export'), params: watchedExportParams }),
  z.object({ kind: z.literal('reporting_coverage'), params: reportingCoverageParams }),
]);
export type AlertRuleExpressionValue = z.infer<typeof alertRuleExpressionSchema>;

// The kind alone, without the parameter shape. Used where the data crosses a boundary
// and the cheap guarantee is that the kind is one we know: the parameters are the
// compiler's job, and it has to read them anyway.
//
// parseExpression used to do `JSON.parse(json) as AlertRuleExpression` on data arriving
// over gRPC. A cast checks nothing; it looked like a guarantee and was not (spec 057 T006).
export const alertRuleExpressionEnvelopeSchema = z.object({
  kind: z.enum(ALERT_RULE_KINDS),
  params: z.record(z.unknown()),
});

export const alertRuleSchema = z.object({
  id: z.string().uuid().optional(),
  name: z
    .string()
    .min(3)
    .max(120)
    .regex(/^[a-z][a-z0-9_]{2,119}$/, 'name must be snake_case'),
  displayName: z.string().min(1).max(200),
  description: z.string().max(2000).nullable().optional(),
  severity: alertSeverityEnum,
  labels: z.record(z.string().min(1).max(80)).default({}),
  expression: alertRuleExpressionSchema,
  evalIntervalSeconds: z.number().int().min(60).max(86400).default(300),
  forDurationSeconds: z.number().int().min(0).max(86400).default(0),
  cooldownSeconds: z
    .number()
    .int()
    .min(0)
    .max(86400 * 7)
    .default(86400),
  notifyOnResolve: z.boolean().default(false),
  templateId: z.string().uuid().nullable().default(null),
  policyId: z.string().uuid().nullable().default(null),
  active: z.boolean().default(true),
});
export type AlertRuleDto = z.infer<typeof alertRuleSchema>;

export const notificationMatcherSchema = z.object({
  label: z.string().min(1).max(80),
  op: z.enum(['eq', 'neq', 'in', 'regex']),
  value: z.union([z.string().min(1).max(200), z.array(z.string().min(1).max(200))]),
});
export type NotificationMatcherDto = z.infer<typeof notificationMatcherSchema>;

export const notificationPolicySchema = z.object({
  id: z.string().uuid().optional(),
  name: z
    .string()
    .min(3)
    .max(120)
    .regex(/^[a-z][a-z0-9_]{2,119}$/, 'name must be snake_case'),
  matchers: z.array(notificationMatcherSchema).default([]),
  channelIds: z.array(z.string().uuid()).default([]),
  groupBy: z.array(z.string().min(1).max(80)).default([]),
  groupWaitSeconds: z.number().int().min(0).max(86400).default(30),
  groupIntervalSeconds: z.number().int().min(0).max(86400).default(300),
  repeatIntervalSeconds: z
    .number()
    .int()
    .min(60)
    .max(86400 * 30)
    .default(86400),
});
export type NotificationPolicyDto = z.infer<typeof notificationPolicySchema>;

export const notificationTemplateSchema = z.object({
  id: z.string().uuid().optional(),
  name: z
    .string()
    .min(3)
    .max(120)
    .regex(/^[a-z][a-z0-9_]{2,119}$/, 'name must be snake_case'),
  titleTpl: z.string().min(1).max(500),
  bodyTpl: z.string().min(1).max(4000),
  locale: z.string().min(2).max(5).default('ro'),
  metadata: z.record(z.unknown()).default({}),
});
export type NotificationTemplateDto = z.infer<typeof notificationTemplateSchema>;

export const alertNotificationChannelSchema = z.object({
  id: z.string().uuid().optional(),
  name: z
    .string()
    .min(3)
    .max(120)
    .regex(/^[a-z][a-z0-9_]{2,119}$/, 'name must be snake_case'),
  kind: notificationChannelKindEnum,
  config: z.record(z.unknown()).default({}),
  active: z.boolean().default(true),
});
export type AlertNotificationChannelDto = z.infer<typeof alertNotificationChannelSchema>;

export const alertEventSchema = z.object({
  id: z.string().uuid(),
  ruleId: z.string().uuid(),
  ruleName: z.string(),
  fingerprint: z.string(),
  status: alertEventStatusEnum,
  severity: alertSeverityEnum,
  labels: z.record(z.string()),
  annotations: z.record(z.unknown()),
  title: z.string(),
  body: z.string(),
  firstFiringAt: z.string().nullable(),
  firedAt: z.string(),
  resolvedAt: z.string().nullable(),
  lastEvaluatedAt: z.string(),
  notificationSendCount: z.number().int().nonnegative(),
  acknowledged: z.boolean(),
  acknowledgedAt: z.string().nullable(),
  acknowledgedBy: z.string().nullable(),
});
export type AlertEventDto = z.infer<typeof alertEventSchema>;

// YAML export envelope — single binding payload for UC-ALRT-08.
export const alertExportEnvelopeSchema = z.object({
  apiVersion: z.literal('simsm.alerts/v1'),
  exportedAt: z.string(),
  rules: z.array(alertRuleSchema),
  templates: z.array(notificationTemplateSchema),
  policies: z.array(notificationPolicySchema),
  channels: z.array(
    alertNotificationChannelSchema.omit({ config: true }).extend({
      config: z.record(z.unknown()),
    }),
  ),
});
export type AlertExportEnvelope = z.infer<typeof alertExportEnvelopeSchema>;
